import type { ReactNode } from 'react';
import { ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import type { IsoDate } from '@/features/manufacturing/planning/dates';
import type { VendorOrderGroup } from '@/features/manufacturing/planning/procurementCalendar';
import type { CalendarEvent, CalendarEventKind, PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency } from '@/lib/formatters';
import { useIsMobile } from '@/lib/useMediaQuery';
import { EventRow, longDate } from './shared';
import { VendorOrderCard } from './VendorOrderCard';

interface ProcurementDaySheetProps {
  date: IsoDate | null;
  model: PlanningModel;
  groups: VendorOrderGroup[];
  events: CalendarEvent[];
  canCreate: boolean;
  onOpenChange: (open: boolean) => void;
  onCreatePo: (group: VendorOrderGroup) => void;
  onCreateDay: (date: IsoDate, groups: VendorOrderGroup[]) => void;
  onOpenItem: (itemId: string) => void;
}

const SECTIONS: Array<{ title: string; kinds: CalendarEventKind[] }> = [
  { title: 'Deadlines and stock-outs', kinds: ['order_latest', 'runout'] },
  { title: 'Draft POs waiting to be placed', kinds: ['po_order_by'] },
  { title: 'Arrivals', kinds: ['receipt_expected', 'planned_receipt'] },
  { title: 'Production', kinds: ['production', 'holiday'] },
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

/** Everything on one day, with a Create PO button per vendor and one for the whole day. */
export function ProcurementDaySheet({
  date,
  model,
  groups,
  events,
  canCreate,
  onOpenChange,
  onCreatePo,
  onCreateDay,
  onOpenItem,
}: ProcurementDaySheetProps) {
  const isMobile = useIsMobile();
  const draftable = groups.filter((g) => !g.unassignable);
  const total = groups.reduce((acc, g) => acc + g.totalCents, 0);

  return (
    <Sheet open={!!date} onOpenChange={(open) => !open && onOpenChange(false)}>
      <SheetContent side={isMobile ? 'bottom' : 'right'} className={isMobile ? undefined : 'sm:max-w-2xl'}>
        {date && (
          <>
            <SheetHeader>
              <SheetTitle>{longDate(date)}</SheetTitle>
              <SheetDescription>
                {date === model.today ? 'Today · ' : ''}
                {model.calendar.isHoliday(date) ? 'Holiday' : model.calendar.isWorkingDay(date) ? 'Working day' : 'Non-working day'}
                {groups.length > 0 && (
                  <>
                    {' · '}
                    {groups.length} {groups.length === 1 ? 'vendor order' : 'vendor orders'}
                    {total > 0 && ` · ${formatCurrency(total)}`}
                  </>
                )}
                {date > model.horizonEnd && ' · beyond the planning horizon'}
              </SheetDescription>
            </SheetHeader>
            <SheetBody className="space-y-6">
              {groups.length === 0 && events.length === 0 && (
                <p className="text-sm text-muted-foreground">Nothing to order or receive on this day.</p>
              )}

              {groups.length > 0 && (
                <Section title={date <= model.today ? 'Order today' : 'Order on this day'}>
                  {canCreate && draftable.length > 1 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">
                      <span>
                        {draftable.length} vendors ·{' '}
                        {formatCurrency(draftable.reduce((acc, g) => acc + g.totalCents, 0))} in one go
                      </span>
                      <Button type="button" size="sm" variant="outline" onClick={() => onCreateDay(date, draftable)}>
                        <ClipboardList className="h-3.5 w-3.5" />
                        Create all {draftable.length} draft POs
                      </Button>
                    </div>
                  )}
                  <div className="space-y-3">
                    {groups.map((group) => (
                      <VendorOrderCard
                        key={group.key}
                        group={group}
                        model={model}
                        canCreate={canCreate}
                        onCreatePo={onCreatePo}
                        onOpenItem={onOpenItem}
                      />
                    ))}
                  </div>
                </Section>
              )}

              {SECTIONS.map(({ title, kinds }) => {
                const rows = events.filter((e) => kinds.includes(e.kind));
                if (rows.length === 0) return null;
                return (
                  <Section key={title} title={title}>
                    <ul className="divide-y divide-border rounded-xl border border-border bg-card px-4">
                      {rows.map((event) => (
                        <EventRow key={event.id} event={event} onOpenItem={onOpenItem} />
                      ))}
                    </ul>
                  </Section>
                );
              })}
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
